import { useForm, useFieldArray } from 'react-hook-form';
import { addParkingLocation } from 'api/canpark/parkingLocations/addParkingLocation';
import { ParkingLocation } from 'containers/pages/CANpark/types/parkingLocation';
import { updateParkingLocation } from 'api/canpark/parkingLocations/updateParkingLocation';
import { getParkingLocationsByCustomerId } from 'api/canpark/parkingLocations/getParkingLocationsByCustomerId';
import { useNotificationProvider } from 'hooks';
import { useState } from 'react';
import { archiveParkingLocation } from 'api/canpark/parkingLocations/archiveParkingLocation';
import { getActiveTourKey } from 'components/onboarding/tour/activeTour';

// The Manage Sites guided tour walks through the whole new-site form but must NOT
// persist — otherwise every run leaves a junk parking site in the DB.
const MANAGE_SITES_TOUR_KEY = 'canpark_manage_sites';

export function useParkingLocationForm(
	selectedParkingLocation: ParkingLocation,
	onSave: (updated: ParkingLocation[]) => void,
	onClose: () => void,
) {
	const [loading, setLoading] = useState(false);
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

	const isNew =
		!selectedParkingLocation || selectedParkingLocation.id === '0';

	const formMethods = useForm<ParkingLocation>({
		defaultValues: selectedParkingLocation,
	});

	const { control } = formMethods;

	const {
		fields: zoneFields,
		append,
		remove,
	} = useFieldArray({
		control,
		name: 'parkingZoneOptions',
	});

	const { notifySuccess } = useNotificationProvider();

	const onSubmit = async (data: ParkingLocation) => {
		// Guided tour: don't create a real site. Just close the drawer — the sync
		// hook then advances the walkthrough to its finish slide.
		if (getActiveTourKey() === MANAGE_SITES_TOUR_KEY) {
			notifySuccess('Sandbox walkthrough complete—no site was created.');
			onClose();
			return;
		}
		try {
			if (isNew) {
				await addParkingLocation(data);
			} else {
				await updateParkingLocation(data);
			}
			const updatedLocations = await getParkingLocationsByCustomerId(
				data.customerID.toString(),
			);
			onSave(updatedLocations);
			notifySuccess('Parking location saved successfully!');
			onClose();
		} catch (err) {
			console.error(err);
			alert('Error saving parking location.');
		}
	};

	const handleDelete = async () => {
		if (!selectedParkingLocation) return;
		setLoading(true);

		try {
			await archiveParkingLocation(selectedParkingLocation.id);
			const updatedList = await getParkingLocationsByCustomerId(
				selectedParkingLocation.customerID.toString(),
			);
			onSave(updatedList);
			onClose();
		} catch (error) {
			alert('Error deleting parking lot.');
		} finally {
			setLoading(false);
		}
	};

	return {
		formMethods,
		zoneFields,
		openConfirmDialog,
		loading,
		setOpenConfirmDialog,
		handleDelete,
		append,
		remove,
		onSubmit,
	};
}
